import {
  SITE_URL,
  LOGO_BCITI,
  LOGO_ATNA,
  OG_IMAGE,
  organization,
  findSeoRoute,
  pageUrl,
  type SeoRoute,
} from '../data/seo'
import { contact, hasPhone, hasEmail } from '../data/contact'
import { platformFaq } from '../data/platformFaq'

type Lang = 'FR' | 'EN'
type Json = Record<string, unknown>

const ORG_ID = `${SITE_URL}/#organization`
const SITE_ID = `${SITE_URL}/#website`
const PLATFORM_ID = `${SITE_URL}/#platform`
const ATNA_ID = `${SITE_URL}/#atna`

const pick = (b: { fr: string; en: string }, lang: Lang) => (lang === 'FR' ? b.fr : b.en)

function organizationNode(lang: Lang): Json {
  return {
    '@type': 'Organization',
    '@id': ORG_ID,
    name: organization.name,
    legalName: organization.legalName,
    alternateName: organization.alternateName,
    url: SITE_URL,
    logo: { '@type': 'ImageObject', url: LOGO_BCITI },
    description: pick(organization.description, lang),
    brand: [
      { '@type': 'Brand', name: 'BCITI', logo: LOGO_BCITI },
      { '@type': 'Brand', name: 'ATNA', logo: LOGO_ATNA },
    ],
    areaServed: { '@type': 'Country', name: 'Canada' },
    knowsLanguage: ['fr-CA', 'en-CA'],
    ...(contact.sameAs.length ? { sameAs: contact.sameAs } : {}),
    ...(hasPhone() || hasEmail()
      ? {
          contactPoint: {
            '@type': 'ContactPoint',
            contactType: 'sales',
            areaServed: 'CA',
            availableLanguage: ['French', 'English'],
            ...(hasPhone() ? { telephone: contact.phone } : {}),
            ...(hasEmail() ? { email: contact.email } : {}),
          },
        }
      : {}),
  }
}

/** FAQPage for the platform overview — mirrors the questions visible on that page. */
function faqNode(url: string, lang: Lang): Json {
  return {
    '@type': 'FAQPage',
    '@id': `${url}#faq`,
    mainEntity: platformFaq.map((item) => ({
      '@type': 'Question',
      name: pick(item.q, lang),
      acceptedAnswer: { '@type': 'Answer', text: pick(item.a, lang) },
    })),
  }
}

function websiteNode(lang: Lang): Json {
  return {
    '@type': 'WebSite',
    '@id': SITE_ID,
    url: SITE_URL,
    name: organization.name,
    inLanguage: ['fr-CA', 'en-CA'],
    publisher: { '@id': ORG_ID },
    description: pick(organization.description, lang),
  }
}

function platformNode(lang: Lang): Json {
  return {
    '@type': 'SoftwareApplication',
    '@id': PLATFORM_ID,
    name: 'BCITI | ATNA',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    url: pageUrl('/platform', lang),
    publisher: { '@id': ORG_ID },
    description: pick(organization.description, lang),
  }
}

function atnaNode(lang: Lang): Json {
  const atna = findSeoRoute('/atna') as SeoRoute
  return {
    '@type': 'SoftwareApplication',
    '@id': ATNA_ID,
    name: 'ATNA',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    url: pageUrl('/atna', lang),
    publisher: { '@id': ORG_ID },
    isPartOf: { '@id': PLATFORM_ID },
    description: pick(atna.description, lang),
  }
}

function breadcrumbNode(route: SeoRoute, lang: Lang): Json | null {
  if (route.path === '/') return null
  const chain: SeoRoute[] = [route]
  let parent = route.parent ? findSeoRoute(route.parent) : undefined
  while (parent) {
    chain.unshift(parent)
    parent = parent.parent ? findSeoRoute(parent.parent) : undefined
  }
  const home = findSeoRoute('/') as SeoRoute
  const items = [home, ...chain]
  return {
    '@type': 'BreadcrumbList',
    itemListElement: items.map((r, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: pick(r.name, lang),
      item: pageUrl(r.path, lang),
    })),
  }
}

/** JSON-LD graph for a route. Every value mirrors content visible on the site. */
export function buildStructuredData(route: SeoRoute, lang: Lang): Json {
  const url = pageUrl(route.path, lang)
  const inLanguage = lang === 'FR' ? 'fr-CA' : 'en-CA'
  const graph: Json[] = [organizationNode(lang), websiteNode(lang)]

  const webPage: Json = {
    '@type': route.kind === 'contact' ? 'ContactPage' : route.path === '/about' ? 'AboutPage' : 'WebPage',
    '@id': `${url}#webpage`,
    url,
    name: pick(route.title, lang),
    description: pick(route.description, lang),
    inLanguage,
    isPartOf: { '@id': SITE_ID },
    publisher: { '@id': ORG_ID },
    primaryImageOfPage: { '@type': 'ImageObject', url: OG_IMAGE },
  }

  switch (route.kind) {
    case 'home':
    case 'platform':
      graph.push(platformNode(lang), atnaNode(lang))
      webPage.about = { '@id': PLATFORM_ID }
      if (route.kind === 'platform') graph.push(faqNode(url, lang))
      break
    case 'intelligence':
      graph.push(platformNode(lang), atnaNode(lang))
      webPage.about = { '@id': ATNA_ID }
      break
    case 'service':
      graph.push(platformNode(lang), {
        '@type': 'Service',
        '@id': `${url}#service`,
        name: pick(route.name, lang),
        serviceType: route.serviceType ? pick(route.serviceType, lang) : pick(route.name, lang),
        description: pick(route.description, lang),
        url,
        provider: { '@id': ORG_ID },
        isRelatedTo: { '@id': PLATFORM_ID },
      })
      webPage.about = { '@id': `${url}#service` }
      break
    case 'caseStudy':
      graph.push({
        '@type': 'Article',
        '@id': `${url}#article`,
        headline: pick(route.title, lang).replace(/\s*\|\s*BCITI \| ATNA$/, ''),
        description: pick(route.description, lang),
        inLanguage,
        image: OG_IMAGE,
        author: { '@id': ORG_ID },
        publisher: { '@id': ORG_ID },
        mainEntityOfPage: { '@id': `${url}#webpage` },
      })
      break
    default:
      break
  }

  const crumbs = breadcrumbNode(route, lang)
  if (crumbs) graph.push(crumbs)
  graph.push(webPage)

  return { '@context': 'https://schema.org', '@graph': graph }
}
